import React, { useCallback } from "react";
import classNames from "classnames";
import { CloseIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { FinancialDocumentBase } from "@common/types/financialDocument";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { useOverlays } from "contexts/OverlayContext";
import styles from "./FinancialDocumentAttachment.module.scss";

interface AttachmentChipProps {
    fileName: string;
    /** Replaces the file name as the visible pill content. */
    label?: React.ReactNode;
    onOpen?: () => void;
    onDetach?: () => void;
    detachLabel?: string;
    testId?: string;
}

export const AttachmentChip: React.FC<AttachmentChipProps> = ({
    fileName,
    label,
    onOpen,
    onDetach,
    detachLabel = "Unlink",
    testId,
}) => (
    <div className={styles.attachmentContainer} data-testid={testId}>
        {onOpen ? (
            <Button
                className={styles.attachmentLink}
                variant="primary"
                size="md"
                onClick={onOpen}
            >
                {label ?? <BreakableText text={fileName} />}
            </Button>
        ) : (
            <Button
                className={classNames(styles.attachmentLink, styles.staticLink)}
                variant="primary"
                size="md"
                asChild
            >
                <span>{label ?? <BreakableText text={fileName} />}</span>
            </Button>
        )}

        {onDetach && (
            <Tooltip content={detachLabel}>
                <Button
                    data-testid="detach-document-button"
                    className={styles.detachButton}
                    variant="primary"
                    size="md"
                    iconOnly
                    onClick={onDetach}
                    aria-label={`${detachLabel} ${fileName}`}
                >
                    <CloseIcon />
                </Button>
            </Tooltip>
        )}
    </div>
);

interface Props {
    financialDocument: Pick<FinancialDocumentBase, "id" | "fileName">;
    detachDocument?: (documentId: number) => void;
    readonly?: boolean;
}

export const FinancialDocumentAttachment: React.FC<Props> = ({
    financialDocument,
    detachDocument,
    readonly = false,
}) => {
    const { openOverlay } = useOverlays();

    const handleOpen = useCallback(() => {
        openOverlay("financialDocumentId", financialDocument.id);
    }, [financialDocument.id, openOverlay]);

    const handleDetach = useCallback(() => {
        detachDocument?.(financialDocument.id);
    }, [financialDocument.id, detachDocument]);

    return (
        <AttachmentChip
            fileName={financialDocument.fileName}
            onOpen={handleOpen}
            onDetach={!readonly && detachDocument ? handleDetach : undefined}
            testId="transaction-document"
        />
    );
};
